"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Image } from "@imagekit/next";
import { useRouter } from "next/navigation";
import {
  HiChevronLeft,
  HiChevronRight,
  HiFire,
  HiLocationMarker,
  HiCurrencyRupee,
  HiCalendar,
  HiX,
  HiUser,
} from "react-icons/hi";
import { Button } from "../../../../components/ui/button";
import { Skeleton } from "../../../../components/ui/skeleton";

const TripSkeleton = () => (
  <div className="max-w-6xl w-full mx-auto px-4 md:px-0">
    <div className="text-center mb-16">
      <Skeleton className="h-4 w-32 mx-auto mb-4" />
      <Skeleton className="h-16 w-3/4 mx-auto mb-4" />
      <Skeleton className="h-4 w-2/3 mx-auto" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 bg-white rounded-[2rem] overflow-hidden shadow-xl lg:min-h-[420px]">
      <div className="lg:col-span-5 relative h-[200px] lg:h-auto">
        <Skeleton className="absolute inset-0" />
      </div>
      <div className="lg:col-span-7 flex flex-col p-6 md:p-12 justify-between">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-6 w-3/4" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-6">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-2 w-12" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <Skeleton className="h-2 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-14 w-full sm:w-48 rounded-xl" />
        </div>
      </div>
    </div>
  </div>
);

const getFormattedLink = (link) => {
  if (!link) return "/trails";
  // Replace legacy or ID-based links with slug-based links
  if (link === "/packages/2" || link === "/trails/2") {
    return "/trails/founders-trail-rishikesh-edition";
  }
  if (link === "/packages/1" || link === "/trails/1") {
    return "/trails/founders-trail-nainital-edition-march";
  }
  return link.replace("/packages/", "/trails/");
};

const TripPopup = ({ trip }) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    // Only show popup for live trips
    if (!trip?.bookingLive) return;

    const hasSeenPopup = sessionStorage.getItem("hasSeenTripPopup");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem("hasSeenTripPopup", "true");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [trip]);

  if (!trip || !trip.bookingLive) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 font-bricolage overflow-hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: "spring", duration: 0.7, bounce: 0.3 }}
            className="relative w-full max-w-4xl bg-white text-black shadow-[0_30px_100px_-20px_rgba(0,0,0,0.5)] overflow-visible flex flex-col md:flex-row max-h-[95vh] md:max-h-none rounded-2xl md:rounded-3xl mx-4"
          >
            {/* Close Button - The "Good" one from previous turn */}
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(false)}
              className="absolute -top-3 -right-3 md:top-6 md:right-6 z-[110] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white text-black shadow-2xl rounded-full border border-gray-100 transition-all cursor-pointer"
            >
              <HiX className="text-xl" />
            </motion.button>

            <div className="flex flex-col md:flex-row w-full overflow-hidden bg-white">
              {/* Image Side */}
              <div className="relative w-full md:w-5/12 h-[200px] md:h-auto overflow-hidden">
                <Image
                  src={trip.img}
                  alt={trip.title}
                  fill
                  className="object-cover"
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:hidden" />
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                      boxShadow: [
                        "0 0 0 0 rgba(220, 38, 38, 0.7)",
                        "0 0 0 10px rgba(220, 38, 38, 0)",
                        "0 0 0 0 rgba(220, 38, 38, 0)",
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                    className="bg-red-600 text-white px-3 py-1 md:px-4 md:py-1.5 text-[8px] md:text-[10px] font-black tracking-[0.2em] uppercase rounded-full shadow-lg"
                  >
                    BOOKING LIVE
                  </motion.div>
                </div>
              </div>

              {/* Content Side */}
              <div className="w-full md:w-7/12 p-6 md:p-12 flex flex-col justify-between bg-white relative">
                <div className="space-y-4 md:space-y-6">
                  <div className="space-y-1 md:space-y-2">
                    <p className="text-[8px] md:text-[10px] tracking-[0.4em] font-bold text-[#FFAE00] uppercase">
                      LATEST EXPERIENCE
                    </p>
                    <h2 className="text-xl md:text-3xl lg:text-4xl font-black leading-tight tracking-tight uppercase">
                      {trip.title}
                    </h2>
                  </div>

                  <div className="space-y-3 md:space-y-4">
                    <div className="flex items-center gap-3 md:gap-4 group">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gray-50 text-[#FFAE00]">
                        <HiLocationMarker className="text-lg group-hover:animate-bounce" />
                      </div>
                      <div>
                        <p className="text-[8px] md:text-[10px] font-bold text-gray-400 tracking-[0.1em] uppercase">Location</p>
                        <p className="font-bold text-xs md:text-base">{trip.location}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 md:gap-4 group">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gray-50 text-[#FFAE00]">
                        <HiCalendar className="text-lg group-hover:rotate-12 transition-transform" />
                      </div>
                      <div>
                        <p className="text-[8px] md:text-[10px] font-bold text-gray-400 tracking-[0.1em] uppercase">Date</p>
                        <p className="font-bold text-sm md:text-base">{trip.fullDate}</p>
                      </div>
                    </div>

                    {trip.mentor && (
                      <div className="flex items-center gap-3 md:gap-4 group">
                        <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-orange-50 text-[#FFAE00]">
                          <HiUser className="text-lg group-hover:scale-110 transition-transform" />
                        </div>
                        <div>
                          <p className="text-[8px] md:text-[10px] font-bold text-gray-400 tracking-[0.1em] uppercase">Mentor</p>
                          <p className="font-bold text-xs md:text-base">{trip.mentor}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 md:mt-12 space-y-4 md:space-y-6">
                  <div className="flex items-end justify-between border-b border-gray-100 pb-3 md:pb-4">
                    <div>
                      <div className="flex items-baseline gap-3">
                        <p className="text-2xl md:text-3xl font-black text-black">{trip.price}</p>
                        {/* <p className="text-sm md:text-base font-bold text-gray-400 line-through">{trip.originalPrice}</p> */}
                      </div>
                    </div>
                    <div className="text-right">
                    </div>
                  </div>

                  <motion.div whileHover="hover" initial="initial" className="relative">
                    <Button
                      onClick={() => {
                        router.push(getFormattedLink(trip.link));
                        setIsOpen(false);
                      }}
                      className="w-full bg-black hover:bg-black text-white rounded-none py-6 md:py-8 font-black tracking-[0.3em] text-[10px] md:text-xs transition-all relative overflow-hidden group border-none"
                    >
                      <motion.span
                        variants={{
                          initial: { x: 0, color: "#FFFFFF" },
                          hover: { x: 10, color: "#000000" }
                        }}
                        className="relative z-20 flex items-center justify-center gap-2"
                      >
                        SECURE YOUR SPOT
                        <HiChevronRight className="text-xl" />
                      </motion.span>
                      <motion.div
                        variants={{
                          initial: { x: "-100%" },
                          hover: { x: 0 }
                        }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-0 bg-[#FFAE00] z-10"
                      />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const LiveTripSlider = () => {
  const router = useRouter();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [trips, setTrips] = useState([
    {
      _id: "default-1",
      title: "FOUNDER’S TRAIL – RISHIKESH",
      location: "RISHIKESH, INDIA",
      price: "₹34,999",
      originalPrice: "₹38,999",
      date: "APRIL 2026",
      fullDate: "16-19 April 2026",
      mentor: "Aditya Arora",
      duration: "3 Nights / 4 Days",
      img: "https://ik.imagekit.io/outcomeouting/OutcomeOuting-assets/outinggroup.jpg?updatedAt=1769979413735",
      spotsLeft: 0,
      bookingLive: false,
      link: "/trails/founders-trail-rishikesh-edition",
      objective: "Deep-hive networking and strategy scaling in the lap of Himalayas."
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = React.useRef(null);

  const folder = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER || "";
  const waitlistUrl = "https://waitlist.outcomeouting.com/";

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await fetch("/api/featured-trips");
        const data = await res.json();
        if (Array.isArray(data)) {
          setTrips(data);
        }
      } catch (error) {
        console.error("Failed to fetch featured trips:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, []);

  const isSliderActive = trips.length > 1;

  useEffect(() => {
    if (!isSliderActive) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % trips.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [isSliderActive, trips.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % trips.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + trips.length) % trips.length);

  if (loading) return (
    <div className="w-full min-h-screen py-24 md:py-32 bg-white flex items-center justify-center">
      <TripSkeleton />
    </div>
  );
  if (trips.length === 0) return null; // Hide section if no featured trips

  // Hide section if no trips are currently live (completed trips shown in TravelPackagesSection)
  const hasLiveTrip = trips.some(t => t.bookingLive === true);
  if (!hasLiveTrip) return null;

  return (
    <div 
      ref={containerRef}
      className="w-full min-h-screen py-24 md:py-32 px-4 md:px-12 flex flex-col items-center justify-center relative overflow-hidden font-bricolage bg-white"
    >
      {/* Premium Background Layering */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Soft Mesh Gradient */}
        <div className="absolute top-0 right-0 w-[60%] h-[70%] bg-gradient-to-bl from-[#FFAE00]/5 via-transparent to-transparent blur-[160px]" />
        <div className="absolute bottom-0 left-0 w-[50%] h-[60%] bg-gradient-to-tr from-gray-100/40 via-transparent to-transparent blur-[120px]" />
        
        {/* Floating Decorative Blobs */}
        <motion.div 
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 10, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFAE00]/5 rounded-full blur-[100px]" 
        />
        
        {/* Abstract Background Floating Text */}
        <div className="absolute inset-0 opacity-[0.03] select-none text-[20vw] font-black pointer-events-none overflow-hidden whitespace-nowrap leading-[0.8]">
          <motion.div 
            animate={{ x: [0, -100, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="flex gap-[10vw]"
          >
            <span>STRATEGY</span>
            <span>OUTCOME</span>
            <span>FOUNDERS</span>
            <span>SCALING</span>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl w-full relative z-10 px-4 md:px-0">
        {/* Section Header - Bold & Balanced */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-[#FFAE00] font-jakarta font-bold text-xs md:text-sm uppercase tracking-[4px]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Exclusive Access
          </motion.span>
          
          <h2 className="font-bricolage font-bold text-[#1B1B1B] text-4xl md:text-6xl lg:text-7xl mt-4 tracking-tight">
            The Founder <span className="text-[#FFAE00]">Mastermind</span>
          </h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="font-jakarta text-gray-500 mt-4 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed"
          >
            High-impact strategy expeditions for founders who have crossed the MVP stage and are ready to dominate.
          </motion.p>
        </motion.div>

        {/* Main Card Container with Outer Glow Aura */}
        <motion.div
           initial={{ opacity: 0, y: 120 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ margin: "-50px" }}
           transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
           className="relative group/card"
        >
          {/* Signature Yellow Aura Background Effect - Enhanced Depth */}
          <div className="absolute -inset-8 bg-[#FFAE00]/10 blur-[100px] rounded-[4rem] opacity-40 group-hover/card:opacity-100 group-hover/card:scale-110 transition-all duration-1000" />
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[90%] h-32 bg-[#FFAE00]/15 blur-[120px] rounded-full pointer-events-none group-hover/card:bg-[#FFAE00]/25 transition-all duration-1000" />

          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              whileHover={{ y: -10 }}
              className="relative p-[1.5px] rounded-2xl md:rounded-[2rem] overflow-hidden group"
            >
              {/* Moving Border Beam Effect */}
              <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  style={{
                    background: "conic-gradient(from 0deg, transparent 70%, #FFAE00 100%)",
                    width: "200%",
                    height: "200%",
                    left: "-50%",
                    top: "-50%",
                  }}
                  className="absolute opacity-100 transition-opacity duration-500"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 bg-white rounded-[inherit] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1),0_20px_40px_-20px_rgba(255,174,0,0.1)] group-hover/card:shadow-[0_80px_160px_-40px_rgba(0,0,0,0.18),0_40px_80px_-20px_rgba(255,174,0,0.25)] transition-all duration-700 overflow-hidden border border-gray-50 lg:min-h-[420px] relative z-10">
                {/* Premium Inner Highlight Line */}
                <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#FFAE00]/30 to-transparent z-20" />
              {/* Left: Visual Side */}
              <div className="lg:col-span-5 relative h-[200px] lg:h-auto overflow-hidden group">
                <motion.div 
                  whileHover={{ scale: 1.15 }}
                  transition={{ duration: 1.5 }}
                  className="absolute inset-0"
                >
                  <Image
                    src={trips[currentSlide].img}
                    alt={trips[currentSlide].title}
                    fill
                    className={`object-cover transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() => setImageLoaded(true)}
                    priority
                  />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                <div className="absolute top-8 left-8">
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 text-white px-4 py-2 text-[9px] font-black tracking-[0.2em] uppercase rounded-lg flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FFAE00] rounded-full" />
                    Limited Spot
                  </div>
                </div>
              </div>

              {/* Right: Rich Content Side */}
              <div className="lg:col-span-7 flex flex-col p-6 md:p-10 lg:p-12 justify-between bg-white relative">
                <div className="space-y-4 md:space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-5">
                      <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "3rem" }}
                        className="h-[1.5px] bg-[#FFAE00]" 
                      />
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] md:text-[11px] font-black tracking-[0.4em] text-[#FFAE00] uppercase font-bricolage">Mission Briefing</span>
                        <div className="flex items-center gap-2.5 bg-red-500/15 px-4 py-1.5 rounded-lg text-[10px] md:text-[11px] font-black tracking-[0.2em] text-red-500 border-2 border-red-500/30 shadow-[0_0_20px_-5px_rgba(239,68,68,0.4)] font-bricolage">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                          </span>
                          LIVE
                        </div>
                      </div>
                    </div>
                    <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-black leading-[0.9] tracking-tighter font-bricolage">
                      {trips[currentSlide].title}
                    </h2>
                    <p className="text-gray-500 text-sm md:text-lg leading-relaxed max-w-2xl font-medium font-bricolage">
                      {trips[currentSlide].objective}
                    </p>
                  </div>

                  {/* Enhanced Deliverables Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 border-y border-gray-50 py-4 md:py-6">
                    {[
                      { label: "Target", val: "Market Scale" },
                      { label: "Mentor", val: trips[currentSlide].mentor },
                      { label: "Outcome", val: "Strategic Roadmap" },
                      { label: "Vibe", val: trips[currentSlide].duration },
                      { label: "Network", val: "Founders" },
                      { label: "Location", val: trips[currentSlide].location.split(',')[0] }
                    ].map((item, idx) => (
                      <div key={item.label} className="flex flex-col gap-2">
                        <p className="text-[9px] font-black text-gray-300 tracking-[0.2em] uppercase">{item.label}</p>
                        <p className="text-xs lg:text-sm font-bold text-black border-l-2 border-gray-100 pl-3">{item.val}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="flex flex-col items-center sm:items-start group">
                    <p className="text-[9px] font-black text-gray-400 tracking-[0.3em] uppercase mb-1">Membership Investment</p>
                    <div className="flex items-baseline gap-4">
                      <span className="text-4xl font-black text-black tracking-tighter group-hover:text-[#FFAE00] transition-colors">{trips[currentSlide].price}</span>
                      {/* <span className="text-gray-200 line-through text-xs font-black">₹{trips[currentSlide].originalPrice.split('₹')[1]}</span> */}
                    </div>
                  </div>

                  <motion.button
                    onClick={() => router.push(getFormattedLink(trips[currentSlide].link))}
                    whileHover={{ scale: 1.05, backgroundColor: "#000", color: "#fff" }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto bg-[#FFAE00] text-black px-12 py-5 font-black text-xs tracking-[0.2em] uppercase rounded-xl transition-all flex items-center justify-center gap-4"
                  >
                    Secure Spot
                    <HiChevronRight className="text-2xl" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

        {/* Designer Navigation */}
        {isSliderActive && (
          <div className="mt-12 flex items-center justify-between px-4">
            <div className="flex gap-4">
              <button
                onClick={prevSlide}
                className="w-12 h-12 flex items-center justify-center bg-white border border-gray-100 rounded-xl text-black/20 hover:text-[#FFAE00] hover:border-[#FFAE00] hover:shadow-[0_10px_30px_-10px_rgba(255,174,0,0.3)] transition-all shadow-sm"
              >
                <HiChevronLeft className="text-xl" />
              </button>
              <button
                onClick={nextSlide}
                className="w-12 h-12 flex items-center justify-center bg-white border border-gray-100 rounded-xl text-black/20 hover:text-[#FFAE00] hover:border-[#FFAE00] hover:shadow-[0_10px_30px_-10px_rgba(255,174,0,0.3)] transition-all shadow-sm"
              >
                <HiChevronRight className="text-xl" />
              </button>
            </div>
            
            <div className="flex gap-3">
              {trips.map((_, i) => (
                <div 
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-1000 overflow-hidden relative ${
                    i === currentSlide ? "w-16 bg-black" : "w-4 bg-gray-100"
                  }`}
                >
                  {i === currentSlide && (
                    <motion.div 
                      layoutId="nav-glow"
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FFAE00]/40 to-transparent"
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {trips[0]?.bookingLive && <TripPopup trip={trips[0]} />}
    </div>
  );
};

export default LiveTripSlider;
